import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import {
  Clock,
  ShieldCheck,
  AlertTriangle,
  Database,
  Timer,
  BadgeCheck,
  Eye,
  MoreHorizontal,
  CheckCircle2,
  Plus,
  MoreVertical,
  ChevronDown,
  TrendingUp,
  Globe2,
  BarChart3,
  CalendarDays,
  FileCheck2,
  Upload,
  Copy,
  ExternalLink,
} from 'lucide-react';

// Updated palette to match your screenshot exactly (Purple, Blue, Green, Orange, Grey)
const COLORS = ['#7c3aed', '#2563eb', '#22c55e', '#f97316', '#cbd5e1'];

// Helper Component for Time Source Details
function TSDetailItem({ label, value, isLink, mono }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-1.5 text-[10px] sm:text-xs last:border-0">
      <span className="font-medium text-slate-500">{label}</span>
      {isLink ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline truncate max-w-[180px]">
          {value}
        </a>
      ) : (
        <span className={`font-medium text-slate-800 truncate max-w-[180px] ${mono ? 'font-mono' : ''}`}>
          {value}
        </span>
      )}
    </div>
  );
}

// Helper Component for Donut Cards
function CompactDonutCard({ title, data }) {
  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-bold text-gray-900">{title}</h2>
        <span className="text-[10px] text-gray-400">
          Total: {data.reduce((acc, curr) => acc + curr.value, 0).toLocaleString()}
        </span>
      </div>
      <div className="flex items-center justify-center gap-4">
        <div className="relative h-[110px] w-[110px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={50} paddingAngle={2}>
                {data.map((entry, index) => (
                  <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1 overflow-y-auto max-h-[110px]">
          {data.map((item, index) => (
            <div key={item.name} className="flex items-center gap-1.5 text-[10px] text-gray-600">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
              <span className="truncate max-w-[100px]" title={item.name}>{item.name}</span>
              <span className="font-semibold text-gray-900 ml-auto">{item.percentage ? `${item.percentage}%` : item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Modal State for Create Timestamp
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [hashAlgo, setHashAlgo] = useState('SHA-256');
  const [tsAuthority, setTsAuthority] = useState('GlobalSign TSA');
  const [notes, setNotes] = useState('');

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0] || e.target.files[0];
    if (file) setUploadedFile(file);
  };

  // Time Sources state
  const [selectedSource, setSelectedSource] = useState('GlobalSign TSA');
  const [expandedCompliance, setExpandedCompliance] = useState(true);

  const timeSourcesData = [
    { name: 'GlobalSign TSA', type: 'Public TSA', authority: 'GlobalSign', status: 'Active', responseTime: '0.38 sec', lastSync: 'May 31, 2025 10:30 AM', url: 'https://timestamp.globalsign.com', apiEndpoint: '/tsa', authentication: 'Mutual TLS', supportedAlgorithms: 'SHA-256, SHA-384, SHA-512', lastSyncFull: 'May 31, 2025 10:30:12 AM (UTC)' },
    { name: 'Sectigo TSA', type: 'Public TSA', authority: 'Sectigo', status: 'Active', responseTime: '0.41 sec', lastSync: 'May 31, 2025 10:29 AM', url: 'https://timestamp.sectigo.com', apiEndpoint: '/tsa', authentication: 'Mutual TLS', supportedAlgorithms: 'SHA-256, SHA-384, SHA-512', lastSyncFull: 'May 31, 2025 10:29:40 AM (UTC)' },
    { name: 'DigiCert TSA', type: 'Public TSA', authority: 'DigiCert', status: 'Active', responseTime: '0.40 sec', lastSync: 'May 31, 2025 10:30 AM', url: 'https://timestamp.digicert.com', apiEndpoint: '/tsa', authentication: 'Mutual TLS', supportedAlgorithms: 'SHA-256, SHA-384, SHA-512', lastSyncFull: 'May 31, 2025 10:30:01 AM (UTC)' },
    { name: 'TSA (Internal)', type: 'Internal TSA', authority: 'Intrust', status: 'Active', responseTime: '0.32 sec', lastSync: 'May 31, 2025 10:29 AM', url: 'https://tsa.internal.local', apiEndpoint: '/tsa', authentication: 'Mutual TLS', supportedAlgorithms: 'SHA-256, SHA-384, SHA-512', lastSyncFull: 'May 31, 2025 10:29:20 AM (UTC)' },
    { name: 'NTP Time Server', type: 'Public NTP', authority: 'NIST', status: 'Active', responseTime: '0.45 sec', lastSync: 'May 31, 2025 10:29 AM', url: 'https://time.nist.gov', apiEndpoint: '/ntp', authentication: 'Network Authentication', supportedAlgorithms: 'NTPv4', lastSyncFull: 'May 31, 2025 10:29:18 AM (UTC)' },
    { name: 'Indian CERT-IN Time Source', type: 'Government', authority: 'CERT-IN', status: 'Active', responseTime: '0.50 sec', lastSync: 'May 31, 2025 10:28 AM', url: 'https://cert-in.org.in', apiEndpoint: '/time', authentication: 'Mutual TLS', supportedAlgorithms: 'SHA-256, SHA-384, SHA-512', lastSyncFull: 'May 31, 2025 10:28:42 AM (UTC)' },
  ];

  const selectedSourceDetails = useMemo(
    () => timeSourcesData.find((s) => s.name === selectedSource) || timeSourcesData[0],
    [selectedSource, timeSourcesData]
  );

  const tsStatusCards = [
    { label: 'Configured Time Sources', value: '6', subtitle: 'All Active', icon: <Clock className="h-4 w-4 text-indigo-600" />, bg: 'bg-indigo-50' },
    { label: 'Trusted Sources', value: '6', subtitle: 'Verified', icon: <ShieldCheck className="h-4 w-4 text-emerald-600" />, bg: 'bg-emerald-50' },
    { label: 'Sync Status', value: '100%', subtitle: 'Healthy', icon: <Globe2 className="h-4 w-4 text-emerald-600" />, bg: 'bg-emerald-50' },
    { label: 'Avg. Response Time', value: '0.42 sec', subtitle: '12.5% vs last month', icon: <TrendingUp className="h-4 w-4 text-blue-600" />, bg: 'bg-blue-50' },
    { label: 'Last Sync', value: 'May 31, 2025', subtitle: '10:30:12 AM (UTC)', icon: <CalendarDays className="h-4 w-4 text-slate-600" />, bg: 'bg-slate-50' },
  ];

  const tsPerformanceData = [
    { date: 'May 26', globalsign: 0.38, sectigo: 0.42, digicert: 0.4, nist: 0.48 },
    { date: 'May 27', globalsign: 0.4, sectigo: 0.41, digicert: 0.38, nist: 0.46 },
    { date: 'May 28', globalsign: 0.36, sectigo: 0.43, digicert: 0.42, nist: 0.5 },
    { date: 'May 29', globalsign: 0.39, sectigo: 0.4, digicert: 0.39, nist: 0.47 },
    { date: 'May 30', globalsign: 0.37, sectigo: 0.44, digicert: 0.41, nist: 0.49 },
    { date: 'May 31', globalsign: 0.38, sectigo: 0.41, digicert: 0.4, nist: 0.45 },
  ];

  const tsHealthData = [
    { name: 'Healthy', value: 6, color: '#16a34a' },
    { name: 'Warning', value: 0, color: '#f59e0b' },
    { name: 'Offline', value: 0, color: '#ef4444' },
  ];

  const complianceStandards = [
    { name: 'RFC 3161 (Time-Stamp Protocol)', icon: <FileCheck2 /> },
    { name: 'ETSI TS 102 023', icon: <FileCheck2 /> },
    { name: 'NIST SP 1800-7', icon: <FileCheck2 /> },
    { name: 'ISO 27001:2022', icon: <FileCheck2 /> },
    { name: 'Indian CERT-IN Guidelines', icon: <FileCheck2 /> },
  ];

  const [selectedRecord, setSelectedRecord] = useState({
    id: 'TS-2025-05-31-001',
    type: 'Document',
    identifier: 'Service_Agreement.pdf',
    hash: 'a3f2c6e5...d4b7e9f1',
    timestamp: 'May 31, 2025 10:30:15 AM (UTC)',
    authority: 'I-PQSign TSA 01',
    status: 'Valid',
    serialNumber: '01A7F9C2D89D4E6FB',
    policyOid: '1.3.6.1.4.1.99999.1.1.1',
    signatureAlgorithm: 'ML-DSA (Dilithium3)',
    signature: '3045022100aaf35dsxf6...c7d6e9f1a2b3c4d5e6f...',
    certificate: 'I-PQSign TSA 01 Certificate',
    chain: 'I-PQSign Root CA > I-PQSign TSA 01 CA > I-PQSign TSA 01',
    blockchainAnchor: 'View on Blockchain',
    ipfsCid: 'QmX7e5g9J8W2nyJ42k9v6b7G3h5T8u1p2L4m6N8o0PqR',
  });

  const tabs = [
    'Overview',
    'Timestamp Records',
    'Verification',
    'Time Sources',
    'Authorities',
    'Settings',
  ];

  const verificationTrendData = [
    { date: 'May 1', verified: 230, failed: 2 },
    { date: 'May 6', verified: 400, failed: 5 },
    { date: 'May 11', verified: 560, failed: 3 },
    { date: 'May 16', verified: 820, failed: 8 },
    { date: 'May 21', verified: 700, failed: 4 },
    { date: 'May 26', verified: 950, failed: 6 },
    { date: 'May 31', verified: 1400, failed: 10 },
  ];

  const trendData = [
    { date: 'May 1', value: 250 },
    { date: 'May 6', value: 420 },
    { date: 'May 11', value: 580 },
    { date: 'May 16', value: 850 },
    { date: 'May 21', value: 720 },
    { date: 'May 26', value: 980 },
    { date: 'May 31', value: 1450 },
  ];

  const typeData = [
    { name: 'Document', value: 6248, percentage: 50.1 },
    { name: 'Artifact / Code', value: 3124, percentage: 25.1 },
    { name: 'Certificate', value: 1856, percentage: 14.9 },
    { name: 'Transaction', value: 856, percentage: 6.9 },
    { name: 'Other', value: 374, percentage: 3.0 },
  ];

  const authorityData = [
    { name: 'I-PQSign TSA 01', value: 6248, percentage: 50.1 },
    { name: 'I-PQSign TSA 02', value: 3124, percentage: 25.1 },
    { name: 'GlobalSign TSA', value: 1856, percentage: 14.9 },
    { name: 'Sectigo TSA', value: 856, percentage: 6.9 },
    { name: 'Other', value: 374, percentage: 3.0 },
  ];

  const recentRecords = [
    {
      id: 'TS-2025-05-31-001',
      type: 'Document',
      identifier: 'Service_Agreement.pdf',
      hash: 'a3f2c6e5...d4b7e9f1',
      timestamp: 'May 31, 2025 10:30:15 AM (UTC)',
      authority: 'I-PQSign TSA 01',
      status: 'Valid',
    },
    {
      id: 'TS-2025-05-31-002',
      type: 'Artifact',
      identifier: 'Firmware_v2.1.bin',
      hash: 'b7e9d2a1...f8c3d5a7',
      timestamp: 'May 31, 2025 10:28:44 AM (UTC)',
      authority: 'I-PQSign TSA 02',
      status: 'Valid',
    },
    {
      id: 'TS-2025-05-31-003',
      type: 'Certificate',
      identifier: 'John_Smith_Cert.pem',
      hash: 'c1a7b8d9...e2f3a4b5',
      timestamp: 'May 31, 2025 10:25:31 AM (UTC)',
      authority: 'GlobalSign TSA',
      status: 'Valid',
    },
    {
      id: 'TS-2025-05-31-004',
      type: 'Document',
      identifier: 'NDA_Contract.pdf',
      hash: 'd5f6e7a8...b9c0d1e2',
      timestamp: 'May 31, 2025 10:23:05 AM (UTC)',
      authority: 'I-PQSign TSA 01',
      status: 'Valid',
    },
    {
      id: 'TS-2025-05-31-005',
      type: 'Artifact',
      identifier: 'Mobile_App_release.apk',
      hash: 'e3d4c5b6...a7b8c9d0',
      timestamp: 'May 31, 2025 10:19:12 AM (UTC)',
      authority: 'Sectigo TSA',
      status: 'Valid',
    },
  ];

  const timeSources = [
    { source: 'GPS Time Server 01', type: 'GPS', status: 'Healthy', lastSync: 'May 31, 2025 10:30:10 AM', offset: '+0.002 sec' },
    { source: 'NTP Pool 01', type: 'NTP', status: 'Healthy', lastSync: 'May 31, 2025 10:30:09 AM', offset: '+0.015 sec' },
    { source: 'NIST Time Server', type: 'NTP', status: 'Healthy', lastSync: 'May 31, 2025 10:30:08 AM', offset: '+0.007 sec' },
    { source: 'Atomic Clock 01', type: 'Atomic', status: 'Healthy', lastSync: 'May 31, 2025 10:30:11 AM', offset: '+0.000 sec' },
    { source: 'NTP Pool 02', type: 'NTP', status: 'Warning', lastSync: 'May 31, 2025 10:30:08 AM', offset: '+0.120 sec' },
    { source: 'GPS Time Server 02', type: 'GPS', status: 'Healthy', lastSync: 'May 31, 2025 10:30:07 AM', offset: '+0.003 sec' },
  ];

  const metrics = [
    { title: 'Timestamps Created', value: '12,458', change: '↑ 18.6% vs last month', Icon: Clock, iconColor: 'text-indigo-600', changeColor: 'text-emerald-600' },
    { title: 'Verified Timestamps', value: '12,201', change: '↑ 17.2% vs last month', Icon: ShieldCheck, iconColor: 'text-emerald-600', changeColor: 'text-emerald-600' },
    { title: 'Failed Verifications', value: '18', change: '↓ 10.0% vs last month', Icon: AlertTriangle, iconColor: 'text-red-500', changeColor: 'text-red-500' },
    { title: 'Active Time Sources', value: '6', change: 'All Healthy', Icon: Database, iconColor: 'text-blue-500', changeColor: 'text-emerald-600' },
    { title: 'Avg. Response Time', value: '0.42 sec', change: '↓ 8.7% vs last month', Icon: Timer, iconColor: 'text-amber-500', changeColor: 'text-emerald-600' },
    { title: 'Trusted Authorities', value: '3', change: 'Configured', Icon: BadgeCheck, iconColor: 'text-indigo-600', changeColor: 'text-emerald-600' },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">
      {/* Header Navigation */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2.5">
          <nav className="flex gap-6">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              return (
                <button
                  key={tab}
                  onClick={() => {
                    if (tab === 'Verification') {
                      navigate('/timestamps/verification');
                    } else if (tab === 'Timestamp Records') {
                      navigate('/timestamps/records');
                    } else {
                      setActiveTab(tabKey);
                    }
                  }}
                  className={`pb-1.5 text-xs font-medium transition-colors ${
                    activeTab === tabKey
                      ? 'border-b-2 border-indigo-600 text-indigo-600'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          {/* Top Right Create Timestamp Button triggers modal */}
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
          >
            + Create Timestamp
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="mx-auto max-w-[1600px] p-0 space-y-4">
      {activeTab === 'time-sources' && (
        <div className="space-y-3">
          {/* Status Cards */}
          <section className="grid grid-cols-2 gap-2 xl:grid-cols-5">
            {tsStatusCards.map((card) => (
              <div key={card.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 shadow-sm">
                <div className="mb-1 flex items-center gap-2">
                  <div className={`rounded-md p-1.5 ${card.bg}`}>{card.icon}</div>
                  <div className="min-w-0">
                    <p className="truncate text-[9px] font-semibold text-slate-500">{card.label}</p>
                    <p className="text-sm font-bold leading-tight text-slate-900">{card.value}</p>
                  </div>
                </div>
                <p className="truncate text-[9px] font-medium text-emerald-600">✓ {card.subtitle}</p>
              </div>
            ))}
          </section>

          {/* Main Content */}
          <section className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            {/* Time Source List */}
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm xl:col-span-7">
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
                <h2 className="text-xs font-bold text-slate-900 sm:text-sm">Time Source List</h2>
                <button className="flex h-7 items-center gap-1 rounded-md bg-indigo-600 px-2.5 text-[10px] font-semibold text-white transition hover:bg-indigo-700 sm:text-xs">
                  <Plus className="h-3 w-3" /> Add Source
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-[10px] sm:text-xs">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {['Name','Type','Authority','Status','Response Time','Last Sync','Actions'].map((h) => (
                        <th key={h} className="px-2.5 py-2 text-left font-semibold text-slate-600">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSourcesData.map((source) => (
                      <tr key={source.name} className="border-b border-slate-100 transition last:border-0 hover:bg-slate-50">
                        <td className="px-2.5 py-2">
                          <button onClick={() => setSelectedSource(source.name)} className={`flex items-center gap-1.5 font-medium ${selectedSource === source.name ? 'text-indigo-700' : 'text-indigo-600'} hover:underline`}>
                            <span className={`h-2 w-2 rounded-full ${selectedSource === source.name ? 'bg-indigo-600' : 'border-2 border-indigo-600'}`} />
                            {source.name}
                          </button>
                        </td>
                        <td className="px-2.5 py-2 text-slate-700">{source.type}</td>
                        <td className="px-2.5 py-2 text-slate-700">{source.authority}</td>
                        <td className="px-2.5 py-2">
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">
                            <CheckCircle2 className="h-2.5 w-2.5" />{source.status}
                          </span>
                        </td>
                        <td className="px-2.5 py-2 font-mono text-slate-700">{source.responseTime}</td>
                        <td className="px-2.5 py-2 text-slate-600">{source.lastSync}</td>
                        <td className="px-2.5 py-2 text-center">
                          <button className="text-slate-400 transition hover:text-slate-700"><MoreVertical className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3 xl:col-span-5">
              {/* Health Card */}
              <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <h3 className="mb-2 text-xs font-bold text-slate-900 sm:text-sm">Time Source Health</h3>
                <div className="flex items-center justify-center gap-5">
                  <div className="relative h-[112px] w-[112px] shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={tsHealthData} dataKey="value" cx="50%" cy="50%" innerRadius={37} outerRadius={52} startAngle={90} endAngle={-270} stroke="none" paddingAngle={0}>
                          {tsHealthData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-base font-bold text-slate-900">100%</span>
                      <span className="text-[9px] font-medium text-slate-600">Healthy</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {tsHealthData.map((item) => (
                      <div key={item.name} className="flex items-center gap-2 text-[10px] text-slate-600 sm:text-xs">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                        <span>{item.name} {item.value} ({((item.value / 6) * 100).toFixed(0)}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Details Card */}
              <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="mb-2.5 flex items-center justify-between border-b border-slate-100 pb-2">
                  <h3 className="text-xs font-bold text-slate-900 sm:text-sm">Time Source Details</h3>
                  <select value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)} className="max-w-[150px] rounded border-0 bg-transparent text-[10px] font-semibold text-indigo-600 outline-none sm:text-xs">
                    {timeSourcesData.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50"><Clock className="h-3.5 w-3.5 text-indigo-600" /></span>
                  <span className="text-xs font-bold text-slate-900">{selectedSourceDetails.name}</span>
                  <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">Active</span>
                </div>
                <div className="space-y-0">
                  <TSDetailItem label="Type" value={selectedSourceDetails.type} />
                  <TSDetailItem label="Authority" value={selectedSourceDetails.authority} />
                  <TSDetailItem label="URL" value={selectedSourceDetails.url} isLink />
                  <TSDetailItem label="API Endpoint" value={selectedSourceDetails.apiEndpoint} mono />
                  <TSDetailItem label="Authentication" value={selectedSourceDetails.authentication} />
                  <TSDetailItem label="Supported Algorithms" value={selectedSourceDetails.supportedAlgorithms} />
                  <TSDetailItem label="Response Time" value={`${selectedSourceDetails.responseTime} (avg)`} />
                  <TSDetailItem label="Last Sync" value={selectedSourceDetails.lastSyncFull} />
                </div>
                <button className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-indigo-600 transition hover:text-indigo-800 sm:text-xs">
                  <ExternalLink className="h-3 w-3" /> View Certificate
                </button>
              </div>
            </div>
          </section>

          {/* Bottom Content */}
          <section className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            {/* Performance */}
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm xl:col-span-7">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-xs font-bold text-slate-900 sm:text-sm">Time Source Performance</h2>
                <BarChart3 className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="h-[190px] w-full sm:h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tsPerformanceData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} />
                    <YAxis domain={[0.2, 0.6]} tick={{ fontSize: 9 }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} width={28} />
                    <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 10 }} />
                    <Legend iconSize={7} wrapperStyle={{ fontSize: 9, paddingTop: 4 }} />
                    <Line type="monotone" dataKey="globalsign" name="GlobalSign" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="sectigo" name="Sectigo" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="digicert" name="DigiCert" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="nist" name="NIST" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Compliance */}
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm xl:col-span-5">
              <button onClick={() => setExpandedCompliance((v) => !v)} className="flex w-full items-center justify-between border-b border-slate-100 pb-2.5">
                <h2 className="text-xs font-bold text-slate-900 sm:text-sm">Compliance & Standards</h2>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expandedCompliance ? 'rotate-180' : ''}`} />
              </button>
              {expandedCompliance && (
                <div className="mt-1">
                  {complianceStandards.map((item) => (
                    <div key={item.name} className="flex items-center justify-between gap-2 border-b border-slate-100 py-2 last:border-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-indigo-600">{React.cloneElement(item.icon, { className: 'h-3.5 w-3.5' })}</span>
                        <span className="truncate text-[10px] font-medium text-slate-700 sm:text-xs">{item.name}</span>
                      </div>
                      <span className="shrink-0 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">Compliant</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
        {activeTab !== 'time-sources' && <>
        {/* Top Metric Cards */}
        <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {metrics.map((m) => {
            const Icon = m.Icon;
            return (
              <div key={m.title} className="rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-gray-50 shrink-0">
                    <Icon className={`h-6 w-6 ${m.iconColor}`} />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{m.title}</p>
                    <p className="mt-1 text-xl font-bold text-gray-900">{m.value}</p>
                    <p className={`mt-1 text-[10px] font-medium ${m.changeColor}`}>{m.change}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Charts Row */}
        <section className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          {/* Timestamp Creation Trend */}
          <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-xs font-bold text-gray-900">Timestamp Creation Trend</h2>
              <button className="rounded-md border border-gray-200 px-2 py-0.5 text-[10px] text-gray-500 hover:bg-gray-50">
                Last 30 Days ▾
              </button>
            </div>

            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={trendData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={{ r: 3, fill: '#6366f1' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Donut Chart 1 */}
          <CompactDonutCard title="Timestamps by Type" data={typeData} />

          {/* Donut Chart 2 */}
          <CompactDonutCard title="Timestamps by Authority" data={authorityData} />
        </section>

        {/* Bottom Section Layout */}
        <section className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          {/* Left Main Column (Spans 2/3) */}
          <div className="flex flex-col gap-3 xl:col-span-2">
            {/* Recent Timestamp Records */}
            <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-xs font-bold text-gray-900">Recent Timestamp Records</h2>
                <button className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">
                  View all
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-left text-[10px]">
                  <thead>
                    <tr className="border-b border-gray-200 text-[9px] uppercase tracking-wider text-gray-400">
                      <th className="py-1.5 px-2">ID</th>
                      <th className="py-1.5 px-2">Type</th>
                      <th className="py-1.5 px-2">File / Reference</th>
                      <th className="py-1.5 px-2">Hash (SHA-256)</th>
                      <th className="py-1.5 px-2">Timestamp</th>
                      <th className="py-1.5 px-2">Authority</th>
                      <th className="py-1.5 px-2">Status</th>
                      <th className="py-1.5 px-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRecords.map((record) => (
                      <tr
                        key={record.id}
                        onClick={() => setSelectedRecord(record)}
                        className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-slate-50/80 ${
                          selectedRecord?.id === record.id ? 'bg-indigo-50/40' : ''
                        }`}
                      >
                        <td className="py-1.5 px-2 font-medium text-gray-900">{record.id}</td>
                        <td className="py-1.5 px-2 text-gray-600">{record.type}</td>
                        <td className="py-1.5 px-2 text-gray-600 font-medium">{record.identifier}</td>
                        <td className="py-1.5 px-2 font-mono text-indigo-600">{record.hash}</td>
                        <td className="py-1.5 px-2 text-gray-600">{record.timestamp}</td>
                        <td className="py-1.5 px-2 text-gray-600">{record.authority}</td>
                        <td className="py-1.5 px-2">
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
                            {record.status}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          <div className="flex items-center justify-end gap-1.5 text-gray-400">
                            <Eye className="h-3.5 w-3.5 hover:text-indigo-600" />
                            <MoreHorizontal className="h-3.5 w-3.5 hover:text-gray-600" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Time Sources & Verification Trend */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {/* Time Sources Status */}
              <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-xs font-bold text-gray-900">Time Sources Status</h2>
                  <button className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">
                    View all
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[10px]">
                    <thead>
                      <tr className="border-b border-gray-200 text-[9px] uppercase tracking-wider text-gray-400">
                        <th className="py-1.5 px-1.5">Source Name</th>
                        <th className="py-1.5 px-1.5">Type</th>
                        <th className="py-1.5 px-1.5">Status</th>
                        <th className="py-1.5 px-1.5">Last Sync</th>
                        <th className="py-1.5 px-1.5 text-right">Offset</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeSources.map((source) => (
                        <tr key={source.source} className="border-b border-gray-100 hover:bg-slate-50">
                          <td className="py-1.5 px-1.5 font-medium text-gray-900">{source.source}</td>
                          <td className="py-1.5 px-1.5 text-gray-500">{source.type}</td>
                          <td className="py-1.5 px-1.5">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                                source.status === 'Healthy'
                                  ? 'bg-emerald-50 text-emerald-600'
                                  : 'bg-amber-50 text-amber-600'
                              }`}
                            >
                              {source.status}
                            </span>
                          </td>
                          <td className="py-1.5 px-1.5 text-gray-500">{source.lastSync}</td>
                          <td className="py-1.5 px-1.5 text-right font-mono text-gray-600">{source.offset}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Verification Trend */}
              <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-xs font-bold text-gray-900">Verification Trend</h2>
                  <button className="rounded-md border border-gray-200 px-2 py-0.5 text-[10px] text-gray-500 hover:bg-gray-50">
                    Last 30 Days ▾
                  </button>
                </div>

                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={verificationTrendData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="verified" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Right Main Column: Detailed Record View (Spans 1/3) */}
          <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm xl:col-span-1">
            <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2.5">
              <div>
                <h2 className="text-xs font-bold text-gray-900">Record Details</h2>
                <p className="text-[10px] font-medium text-indigo-600">{selectedRecord.id}</p>
              </div>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[9px] font-semibold text-emerald-600">
                {selectedRecord.status}
              </span>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Asset Type</span>
                <span className="font-medium text-gray-900">{selectedRecord.type}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">File Name</span>
                <span className="font-medium text-gray-900">{selectedRecord.identifier}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Timestamp Authority</span>
                <span className="font-medium text-gray-900">{selectedRecord.authority}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Timestamp</span>
                <span className="font-medium text-gray-900 text-right">{selectedRecord.timestamp}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Serial Number</span>
                <span className="font-mono text-[10px] text-gray-800">{selectedRecord.serialNumber}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Signature Alg</span>
                <span className="font-medium text-indigo-600">{selectedRecord.signatureAlgorithm}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">Policy OID</span>
                <span className="font-mono text-[10px] text-gray-800">{selectedRecord.policyOid}</span>
              </div>
              <div className="pt-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">SHA-256 Hash</p>
                <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2 font-mono text-[10px] text-indigo-600">
                  <span className="truncate">{selectedRecord.hash}</span>
                  <Copy className="h-3.5 w-3.5 cursor-pointer text-gray-400 hover:text-gray-600 shrink-0 ml-2" />
                </div>
              </div>
              <div className="pt-1 flex items-center justify-between">
                <button className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">
                  <ExternalLink className="h-3.5 w-3.5" /> View Certificate
                </button>
                <button className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800">
                  <Database className="h-3.5 w-3.5" /> {selectedRecord.blockchainAnchor}
                </button>
              </div>
            </div>
          </div>
        </section>
      </>}
      </main>

      {/* CREATE TIMESTAMP POPUP MODAL */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl border border-slate-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <Clock className="h-4 w-4" />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Create TimeStamp</h3>
                  <p className="text-[10px] text-slate-400">Add a trusted timestamp tp prove the existence and integrity of your digital asset.</p>
                </div>
              </div>
              <button
                onClick={() => { setIsCreateModalOpen(false); setUploadedFile(null); setNotes(''); }}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="mt-2 space-y-2">
              {/* File Upload */}
              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  File Upload <span className="text-red-500">*</span>
                </label>
                <label
                  className="mt-0.5 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/30 px-3 py-3 text-center hover:bg-indigo-50/60 transition"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                >
                  <input type="file" className="hidden" onChange={handleFileDrop} />
                  <Upload className="h-4 w-4 text-indigo-500" />
                  <p className="text-xs font-semibold text-indigo-700">Drag & drop or <span className="underline">click to browse</span></p>
                  <p className="text-[10px] text-slate-400">PDF, DOC, ZIP, BIN, TAR… (Max 50 MB)</p>
                </label>
              </div>

              {/* File Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700">File Name</label>
                <div className="mt-0.5 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/30 px-3 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileCheck2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span className="text-xs font-medium text-slate-800 truncate">
                      {uploadedFile ? uploadedFile.name : 'No file selected'}
                    </span>
                    {uploadedFile && (
                      <span className="text-[10px] text-slate-400 shrink-0">({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
                    )}
                  </div>
                  <CheckCircle2 className={`h-3.5 w-3.5 shrink-0 ${uploadedFile ? 'text-emerald-600' : 'text-slate-300'}`} />
                </div>
              </div>

              {/* Hash Algorithm */}
              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Hash Algorithm <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-0.5">
                  <select
                    value={hashAlgo}
                    onChange={(e) => setHashAlgo(e.target.value)}
                    className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-600 focus:outline-none"
                  >
                    <option>SHA-256</option>
                    <option>SHA-384</option>
                    <option>SHA-512</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Timestamp Authority */}
              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Timestamp Authority <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-0.5">
                  <select
                    value={tsAuthority}
                    onChange={(e) => setTsAuthority(e.target.value)}
                    className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-indigo-600 focus:outline-none"
                  >
                    <option>GlobalSign TSA</option>
                    <option>Sectigo TSA</option>
                    <option>DigiCert TSA</option>
                    <option>I-PQSign TSA 01</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Additional Details */}
              <div>
                <label className="block text-xs font-semibold text-slate-700">
                  Additional Details (Optional)
                </label>
                <div className="relative mt-0.5">
                  <textarea
                    rows={2}
                    maxLength={500}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any reference, description or notes..."
                    className="w-full rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-800 focus:border-indigo-600 focus:outline-none resize-none"
                  />
                  <span className="absolute bottom-1.5 right-2.5 text-[10px] text-slate-400">{notes.length}/500</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-2.5">
              <button
                onClick={() => { setIsCreateModalOpen(false); setUploadedFile(null); setNotes(''); }}
                className="rounded-lg border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                disabled={!uploadedFile}
                onClick={() => { setIsCreateModalOpen(false); setUploadedFile(null); setNotes(''); }}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Clock className="h-3.5 w-3.5" /> Create TimeStamp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}